import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  Table as TanStackTable,
  useReactTable,
} from '@tanstack/react-table'
import { format, isValid } from 'date-fns'
import { IconV2CorrectOutline } from '@icons/IconV2CorrectOutline'
import { IconV2IncorrectOutline } from '@icons/IconV2IncorrectOutline'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { useMemo, useState } from 'react'

import { AiTableCellObject, AiTableCellValue, AiTableRow, AiTableScalar, AiTableSchema } from './types-ai-table'
import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import { ComponentActionsContainer } from '@/catalog-apps/components/component-library/shared/ComponentActionsContainer'
import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { ExportButtonV2 } from '@/catalog-apps/components/component-library/DataTableV2/components/ExportButtonV2'
import { DataTableV2DataRow } from '@/catalog-apps/components/component-library/DataTableV2/types'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { cn } from '@/lib/utils'

const PREVIEW_ITEM_COUNT = 3
const OBJECT_PREVIEW_ENTRY_COUNT = 1

function isDateString(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return false
  }

  return isValid(new Date(value))
}

function formatScalarValue(value: AiTableScalar): string {
  if (value === null || value === undefined) {
    return '-'
  }

  if (typeof value === 'string') {
    return isDateString(value) ? format(new Date(value), 'PPp') : value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  return String(value)
}

function formatObjectValue(value: AiTableCellObject): string {
  return Object.entries(value)
    .map(([key, objectValue]) => `${key}: ${formatScalarValue(objectValue)}`)
    .join(', ')
}

function formatCellValue(value: AiTableCellValue): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          return formatObjectValue(item)
        }

        return formatScalarValue(item)
      })
      .join(', ')
  }

  if (typeof value === 'object' && value !== null) {
    return formatObjectValue(value)
  }

  return formatScalarValue(value)
}

function getSortableValue(value: AiTableCellValue) {
  if (typeof value === 'number') {
    return value
  }

  if (typeof value === 'string' && isDateString(value)) {
    return new Date(value).getTime()
  }

  return formatCellValue(value).toLowerCase()
}

function renderScalarChip(value: AiTableScalar, index: number) {
  return (
    <span
      key={`${formatScalarValue(value)}-${index}`}
      className="rounded-l-full rounded-r-full bg-muted px-1.5 py-0.5 text-xs"
    >
      {formatScalarValue(value)}
    </span>
  )
}

function renderObjectSummary(value: AiTableCellObject) {
  const titleEntry =
    Object.entries(value).find(([key]) => ['title', 'name', 'label'].includes(key.toLowerCase())) ??
    Object.entries(value)[0]

  if (!titleEntry) {
    return 'Empty object'
  }

  const [key, objectValue] = titleEntry

  return `${key}: ${formatScalarValue(objectValue)}`
}

function CellValue({
  value,
  isExpanded,
  onToggle,
}: {
  value: AiTableCellValue
  isExpanded: boolean
  onToggle: () => void
}) {
  const formattedValue = formatCellValue(value)

  if (typeof value === 'string') {
    if (isDateString(value)) {
      return <span className="block whitespace-nowrap">{formattedValue}</span>
    }

    return (
      <button
        type="button"
        className={cn('block w-full text-left', !isExpanded && 'truncate')}
        title={!isExpanded ? formattedValue : undefined}
        onClick={onToggle}
      >
        {formattedValue}
      </button>
    )
  }

  if (typeof value === 'boolean') {
    return value ? (
      <IconV2CorrectOutline size={18} className="text-success" aria-label="True" />
    ) : (
      <IconV2IncorrectOutline size={18} className="text-error" aria-label="False" />
    )
  }

  if (!Array.isArray(value) && (typeof value !== 'object' || value === null)) {
    return <span className="block whitespace-nowrap">{formattedValue}</span>
  }

  if (Array.isArray(value)) {
    const visibleItems = (isExpanded ? value : value.slice(0, PREVIEW_ITEM_COUNT)) as (
      | AiTableScalar
      | AiTableCellObject
    )[]
    const hiddenItemCount = value.length - visibleItems.length

    if (value.length === 0) {
      return <span className="text-muted-foreground">Empty</span>
    }

    if (value.every((item) => typeof item !== 'object' || item === null)) {
      return (
        <button type="button" className="block w-full text-left" title={formattedValue} onClick={onToggle}>
          <span className={cn('flex flex-wrap gap-1', !isExpanded && 'max-h-6 overflow-hidden')}>
            {visibleItems.map((item, index) => renderScalarChip(item as AiTableScalar, index))}
            {hiddenItemCount > 0 && (
              <span className="rounded-l-full rounded-r-full bg-muted px-1.5 py-0.5 text-xs">
                +{hiddenItemCount} more
              </span>
            )}
          </span>
        </button>
      )
    }

    if (!isExpanded) {
      const firstItem = value[0] as AiTableCellObject

      return (
        <button type="button" className="block w-full text-left" title={formattedValue} onClick={onToggle}>
          <span className="flex max-w-64 items-center gap-1">
            <span className="shrink-0 rounded-l-full rounded-r-full bg-muted px-1.5 py-0.5 text-xs">
              {value.length} {value.length === 1 ? 'item' : 'items'}
            </span>
            <span className="truncate rounded-l-full rounded-r-full border bg-muted/30 px-1.5 py-0.5 text-xs">
              {renderObjectSummary(firstItem)}
            </span>
            {value.length > 1 && (
              <span className="shrink-0 rounded-l-full rounded-r-full bg-muted px-1.5 py-0.5 text-xs">
                +{value.length - 1} more
              </span>
            )}
          </span>
        </button>
      )
    }

    return (
      <button type="button" className="block w-full text-left" title={formattedValue} onClick={onToggle}>
        <span className="flex max-w-80 flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {value.length} {value.length === 1 ? 'item' : 'items'}
          </span>
          {visibleItems.map((item, index) => (
            <span key={`${formatCellValue(item)}-${index}`} className="rounded border bg-muted/30 px-2 py-1 text-xs">
              {formatObjectValue(item as AiTableCellObject)}
            </span>
          ))}
          {hiddenItemCount > 0 && <span className="text-xs text-muted-foreground">+{hiddenItemCount} more</span>}
        </span>
      </button>
    )
  }

  const entries = Object.entries(value)
  const visibleEntries = isExpanded ? entries : entries.slice(0, OBJECT_PREVIEW_ENTRY_COUNT)
  const hiddenEntryCount = entries.length - visibleEntries.length

  return (
    <button type="button" className="block w-full text-left" title={formattedValue} onClick={onToggle}>
      <span className="flex max-w-64 flex-wrap gap-1">
        {visibleEntries.map(([key, objectValue]) => (
          <span key={key} className="rounded-l-full rounded-r-full border bg-muted/30 px-1.5 py-0.5 text-xs">
            <span className="font-medium">{key}:</span> {formatScalarValue(objectValue)}
          </span>
        ))}
        {hiddenEntryCount > 0 && (
          <span className="rounded-l-full rounded-r-full bg-muted px-1.5 py-0.5 text-xs">+{hiddenEntryCount} more</span>
        )}
      </span>
    </button>
  )
}

export function AiTable({ schema }: { schema: AiTableSchema }) {
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set())

  const exportData = useMemo<DataTableV2DataRow[]>(
    () =>
      schema.rows.map((row) => {
        return schema.columns.reduce<DataTableV2DataRow>(
          (exportRow, column) => {
            exportRow[column.title] = formatCellValue(row[column.id])
            return exportRow
          },
          { id: row.id }
        )
      }),
    [schema]
  )

  const columns = useMemo<ColumnDef<AiTableRow>[]>(
    () =>
      schema.columns.map((column) => ({
        id: column.id,
        accessorKey: column.id,
        header: column.title,
        sortingFn: (firstRow, secondRow) => {
          const firstValue = getSortableValue(firstRow.getValue<AiTableCellValue>(column.id))
          const secondValue = getSortableValue(secondRow.getValue<AiTableCellValue>(column.id))

          if (typeof firstValue === 'number' && typeof secondValue === 'number') {
            return firstValue - secondValue
          }

          return String(firstValue).localeCompare(String(secondValue), undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        },
        cell: ({ cell, row }) => {
          const value = cell.getValue<AiTableCellValue>()
          const cellId = `${row.id}-${column.id}`
          const isExpanded = expandedCells.has(cellId)

          return (
            <CellValue
              value={value}
              isExpanded={isExpanded}
              onToggle={() => {
                setExpandedCells((currentExpandedCells) => {
                  const nextExpandedCells = new Set(currentExpandedCells)

                  if (nextExpandedCells.has(cellId)) {
                    nextExpandedCells.delete(cellId)
                  } else {
                    nextExpandedCells.add(cellId)
                  }

                  return nextExpandedCells
                })
              }}
            />
          )
        },
      })),
    [expandedCells, schema]
  )

  const table = useReactTable({
    data: schema.rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: (row, columnId, filterValue) => {
      const searchValue = String(filterValue).toLowerCase()
      const cellValue = formatCellValue(row.getValue<AiTableCellValue>(columnId)).toLowerCase()

      return cellValue.includes(searchValue)
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: {
      globalFilter,
      sorting,
    },
  })

  return (
    <div className="flex max-h-[50vh] min-h-0 w-full flex-col overflow-hidden">
      <ComponentHeader emphasize wrapperClassName="rounded-b-none">
        <ComponentTitle title={schema.title} description={schema.description} />
        <ComponentActionsContainer className="bg-transparent p-0">
          <ExportButtonV2
            table={table as unknown as TanStackTable<DataTableV2DataRow>}
            data={exportData}
            title={schema.title}
            selectionRequired={false}
            className="text-background dark:text-foreground"
          />
          <SearchInput value={globalFilter} onChange={setGlobalFilter} />
        </ComponentActionsContainer>
      </ComponentHeader>
      <div className="min-h-0 flex-1 overflow-auto rounded-md border bg-background">
        <table className="w-full min-w-max caption-bottom border-collapse text-sm">
          <TableHeader className="sticky top-0 z-10 bg-offwhite">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sortDirection = header.column.getIsSorted()
                  const columnDescription = schema.columns.find((column) => column.id === header.column.id)?.description
                  const headerButton = (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 text-left"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sortDirection === 'asc' ? (
                        <ArrowUp className="size-3.5" />
                      ) : sortDirection === 'desc' ? (
                        <ArrowDown className="size-3.5" />
                      ) : (
                        <ChevronsUpDown className="size-3.5 opacity-60" />
                      )}
                    </button>
                  )

                  return (
                    <TableHead key={header.id} className="whitespace-nowrap font-bold">
                      {header.isPlaceholder ? null : columnDescription ? (
                        <TooltipButton tooltip={columnDescription} side="bottom" contentClassName="max-w-64">
                          {headerButton}
                        </TooltipButton>
                      ) : (
                        headerButton
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="even:bg-offwhite/60 hover:bg-primary/5">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="max-w-64 py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>
    </div>
  )
}
